import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import PublicLayout from "@/components/layout/PublicLayout";
import SectionEyebrow from "@/components/common/SectionEyebrow";
import { publicApi } from "@/lib/api";
import { ArrowRight, Quote } from "lucide-react";

export default function Founder() {
  const [f, setF] = useState(null);
  const [books, setBooks] = useState([]);
  useEffect(() => {
    publicApi.founder().then(setF).catch(() => {});
    publicApi.books().then(setBooks).catch(() => {});
  }, []);
  const visibleRecognitions = useMemo(
    () => (f?.recognitions || []).filter((r) => (r.status || "published") !== "draft"),
    [f]
  );
  if (!f) return <PublicLayout />;

  return (
    <PublicLayout title="Founder — Dr. Saurabh Katiyar">
      <section className="relative bsca-dark bsca-cosmic bsca-grain -mt-16 md:-mt-20 lg:-mt-[88px] pt-32 md:pt-40 pb-24 overflow-hidden">
        <div className="bsca-field-lines" />
        <div className="relative max-w-7xl mx-auto px-6 md:px-10 lg:px-14 grid md:grid-cols-12 gap-12 items-center">
          <div className="md:col-span-5">
            <div className="aspect-[4/5] relative overflow-hidden bg-[#0A1120]">
              <img src={f.portrait_image} alt={f.name} className="absolute inset-0 w-full h-full object-cover" />
              <div className="absolute inset-0 ring-1 ring-inset ring-white/10" />
            </div>
          </div>
          <div className="md:col-span-7 bsca-fade-up">
            <SectionEyebrow dark>Founder</SectionEyebrow>
            <h1 className="font-serif text-white text-4xl md:text-6xl tracking-tight leading-[1.02]">{f.name}</h1>
            <div className="mt-3 text-[#D4AF37] text-lg">{f.title}</div>
            <p className="mt-7 text-white/80 leading-relaxed text-lg max-w-2xl">{f.short_bio}</p>
            <Quote className="mt-10 h-6 w-6 text-[#D4AF37]" strokeWidth={1.2} />
            <blockquote className="mt-3 font-serif text-2xl md:text-3xl text-white max-w-3xl">
              {`"${f.quote}"`}
            </blockquote>
          </div>
        </div>
      </section>

      <section className="bg-[#F8F6F1] py-24">
        <div className="max-w-4xl mx-auto px-6 md:px-10 prose-bsca">
          <SectionEyebrow>Biography</SectionEyebrow>
          <div className="text-[#1f2937] leading-relaxed text-lg whitespace-pre-line">{f.long_bio}</div>
        </div>
      </section>

      <section className="bg-white py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-14 grid md:grid-cols-3 gap-px bg-black/10 border border-black/10 bsca-stagger">
          {(f.sections || []).map((s, i) => (
            <div key={s.title} data-testid={`founder-section-${i}`} className="bg-white p-8">
              <div className="bsca-eyebrow text-[#7A5A06] text-[10px]">0{i + 1}</div>
              <h3 className="mt-3 font-serif text-2xl">{s.title}</h3>
              <p className="mt-3 text-[#4B5563] leading-relaxed">{s.body}</p>
            </div>
          ))}
        </div>
      </section>

      {f.speaking_image && (
        <section data-testid="founder-speaking" className="bsca-dark bsca-cosmic bsca-grain py-20 md:py-28 relative overflow-hidden">
          <div className="bsca-field-lines" />
          <div className="relative max-w-7xl mx-auto px-6 md:px-10 lg:px-14 grid md:grid-cols-12 gap-12 items-center">
            <div className="md:col-span-7">
              <div className="aspect-[4/3] relative overflow-hidden bg-[#0A1120] shadow-2xl">
                <img
                  src={f.speaking_image}
                  alt={f.speaking_alt || `${f.name} delivering a keynote address.`}
                  data-testid="founder-speaking-img"
                  className="absolute inset-0 w-full h-full object-cover"
                  loading="lazy"
                />
                <div className="absolute inset-0 ring-1 ring-inset ring-white/10" />
              </div>
            </div>
            <div className="md:col-span-5">
              <SectionEyebrow dark>On the public stage</SectionEyebrow>
              <h2 className="font-serif text-white text-3xl md:text-4xl tracking-tight leading-tight">
                {f.speaking_caption || "From writing to wider public dialogue."}
              </h2>
              <p className="mt-5 text-white/70 leading-relaxed">
                Dr. Katiyar speaks publicly on cosmology, consciousness, the future of intelligence, and the integration of science with wisdom — bridging research with broader audiences across keynotes, interviews, and BSCA events.
              </p>
              <Link to="/media" data-testid="link-founder-media" className="mt-7 inline-flex items-center text-[#D4AF37] bsca-link">
                Explore Media & Insights <ArrowRight className="h-4 w-4 ml-1" />
              </Link>
            </div>
          </div>
        </section>
      )}

      {visibleRecognitions.length > 0 && (
        <section data-testid="founder-recognitions" className="bg-white py-20 md:py-24">
          <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-14">
            <SectionEyebrow>Selected Recognitions</SectionEyebrow>
            <h2 className="font-serif text-3xl md:text-4xl tracking-tight">Independent recognitions on record.</h2>
            <p className="mt-3 text-[#4B5563] text-sm max-w-2xl">Recognition images shown as proof of source. We present these conservatively; titles match the wording on each publication.</p>
            <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-6 bsca-stagger">
              {visibleRecognitions.map((r) => (
                <article key={`${r.title}-${r.source}`} data-testid={`recognition-${r.title}`} className="bg-[#F8F6F1] border border-black/10 overflow-hidden group">
                  <div className="aspect-[4/3] relative overflow-hidden bg-white">
                    <img
                      src={r.image}
                      alt={r.alt || r.title}
                      loading="lazy"
                      className="absolute inset-0 w-full h-full object-contain p-4 transition-transform duration-700 group-hover:scale-[1.02]"
                    />
                  </div>
                  <div className="p-6">
                    <div className="bsca-eyebrow text-[#7A5A06] text-[10px]">{r.source}{r.year ? ` · ${r.year}` : ""}</div>
                    <h3 className="mt-2 font-serif text-xl leading-tight">{r.title}</h3>
                    <div className="mt-3 inline-flex items-center text-[10px] bsca-eyebrow text-[#7A5A06]">Verified source on file</div>
                  </div>
                </article>
              ))}
            </div>
          </div>
        </section>
      )}

      <section className="bg-[#F8F6F1] py-20">
        <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-14 grid md:grid-cols-2 gap-12">
          <div className="bg-white border border-black/10 p-8">
            <SectionEyebrow>Awards & Recognition</SectionEyebrow>
            {(f.awards || []).length > 0 ? (
              <ul className="mt-2 space-y-4">
                {f.awards.map((a) => (
                  <li key={`${a.title}-${a.year || ""}`} className="border-l-2 border-[#D4AF37] pl-4">
                    <div className="font-serif text-lg leading-tight">{a.title}</div>
                    <div className="text-xs text-[#4B5563] mt-1">{a.publisher} · {a.type} · {a.year}</div>
                    {a.verified && (
                      <div className="mt-1 inline-flex items-center text-[10px] bsca-eyebrow text-[#7A5A06]">Verified source on file</div>
                    )}
                  </li>
                ))}
              </ul>
            ) : null}
            <p className="mt-4 text-[#4B5563] italic text-sm">{f.awards_note}</p>
            {f.verification_url && (
              <a href={f.verification_url} target="_blank" rel="noopener noreferrer" className="mt-5 inline-flex items-center bsca-link text-sm">
                Independent profile reference <ArrowRight className="h-3 w-3 ml-1" />
              </a>
            )}
          </div>
          <div className="bg-white border border-black/10 p-8">
            <SectionEyebrow>Selected Media & Talks</SectionEyebrow>
            <p className="text-[#4B5563] italic">{f.media_note}</p>
            <Link to="/media" className="mt-6 inline-flex items-center bsca-link text-sm">Visit Media & Insights <ArrowRight className="h-3 w-3 ml-1" /></Link>
          </div>
        </div>
      </section>

      <section className="bg-white py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-14">
          <SectionEyebrow>The Beyond Science Series</SectionEyebrow>
          <h2 className="font-serif text-3xl md:text-4xl tracking-tight">Authored by Dr. Saurabh Katiyar.</h2>
          <div className="mt-10 grid grid-cols-2 md:grid-cols-5 gap-6">
            {books.map((b) => (
              <Link to={`/beyond-science/${b.slug}`} key={b.slug} className="group bsca-lift">
                <div className="aspect-[3/4] relative bg-[#0A1120] overflow-hidden">
                  <img src={b.cover_image} alt={b.title} className="absolute inset-0 w-full h-full object-cover" />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#050B14]/70 to-transparent" />
                  <div className="absolute bottom-3 left-3 right-3">
                    <div className="bsca-eyebrow text-[#D4AF37] text-[9px]">Vol. {b.volume}</div>
                    <div className="font-serif text-white text-sm leading-tight mt-1">{b.title}</div>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </PublicLayout>
  );
}
